import React, { useEffect, useMemo, useState } from 'react';
import { FormikHelpers } from 'formik';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthContext } from 'contexts/AuthContext';
import {
    NSButton, NSInput, NSLabel, NSSelect,
} from 'bricks';
import NSModal, { INSModalProps } from 'bricks/NSModal/NSModal';
import { IOptionNumberValue } from 'bricks/types';
import RenderIf from 'components/RenderIf/RenderIf';
import { camelToSnakeCase } from 'ns_libs/formatter';
import { BOESlideoutFormValues } from '../SlideoutForm/types';
import { useGetDeals } from '../../../../Deals/hooks/useFetchDeals';
import { useGetBOEScenarios } from '../../../../ScenarioComparison/hooks/useGetBOEScenarios';

export interface IImportUsesModalProps extends INSModalProps {
    dealId?: number | null;
    onConfirm: (createNewDeal?: boolean) => void;
    onCancel: () => void;
    setFieldValue?: FormikHelpers<BOESlideoutFormValues>['setFieldValue'];
    selectedScenarioName?: string;
}

const SaveModal = ({
    onConfirm, onCancel, setFieldValue, children, dealId = null, selectedScenarioName = '', ...props
}: IImportUsesModalProps) => {
    const queryClient = useQueryClient();
    const { selectedOrganizationId } = useAuthContext();

    const [scenarioName, setScenarioName] = useState<string>(selectedScenarioName);
    const [selectedDeal, setSelectedDeal] = useState<IOptionNumberValue | undefined>(undefined);

    // TODO If dropdownDeals is more than 100, current dropdown select will no longer be useful.
    // TODO Better to swap with a Input field with dropdown suggestion filter
    const { data: dropdownDeals } = useGetDeals({
        pageSize: 100,
        currentPage: 1,
        orgId: selectedOrganizationId!,
        sortOrder: 'asc',
        sortBy: camelToSnakeCase('dealName'),
    });
    
    const { data: dealScenarios } = useGetBOEScenarios(
        selectedOrganizationId!,
        selectedDeal?.value || undefined,
    );

    const { data: organizationScenarios } = useGetBOEScenarios(
        selectedOrganizationId!,
        undefined,
    );

    const dealOptions = useMemo(() => dropdownDeals?.items?.map(item => ({
        value: item.id,
        label: item.dealName,
    })) || [], [dropdownDeals?.items]);

    const validationError = useMemo(() => {
        if (!scenarioName.trim()) return null;

        // Check if scenario name already exists for the selected deal
        if (selectedDeal && dealScenarios) {
            const isDuplicate = dealScenarios.some(
                scenario => scenario.name.toLowerCase() === scenarioName.toLowerCase(),
            );
            if (isDuplicate) {
                return 'Scenario name must be unique.';
            }
        }

        // If no deal is selected, validate against organization scenarios without deal_id
        if (!selectedDeal && organizationScenarios) {
            const uncategorizedScenarios = organizationScenarios.filter(
                scenario => !scenario.dealId,
            );
            const isDuplicate = uncategorizedScenarios.some(
                scenario => scenario.name.toLowerCase() === scenarioName.toLowerCase(),
            );
            if (isDuplicate) {
                return 'Scenario name must be unique.';
            }
        }

        return null;
    }, [scenarioName, selectedDeal, dealScenarios, organizationScenarios]);

    useEffect(() => {
        if (!props.isOpen) {
            setScenarioName('');
        } else if (dealId !== selectedDeal?.value) {
            handleDealSelection(dealId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isOpen, dealId, dealOptions]);

    const handleCancel = () => {
        onCancel();
        setScenarioName('');
    };

    const handleDealSelection = (id?: number | null) => {
        const currentDeal = id ? dropdownDeals?.items?.find(deal => deal.id === id) : undefined;
        if (!currentDeal) {
            setSelectedDeal(undefined);
            setScenarioName(selectedScenarioName || '');
        } else {
            const defaultScenarioName = `Scenario${currentDeal?.numberOfBoeScenarios ? currentDeal.numberOfBoeScenarios + 1 : 1}`;
            
            if (!scenarioName || scenarioName === selectedScenarioName) {
                setScenarioName(selectedScenarioName || defaultScenarioName);
            }

            setSelectedDeal({
                value: currentDeal.id,
                label: currentDeal.dealName,
            });
        }
    };

    const handleSave = (createNewDeal: boolean = false) => {
        if (validationError) {
            return;
        }

        try {
            const selectedDealToSave = dealOptions?.find(deal => deal.value === selectedDeal?.value);

            if (setFieldValue) {
                setFieldValue('name', scenarioName);
                setFieldValue('dealId', selectedDealToSave?.value);

                setTimeout(() => {
                    onConfirm(createNewDeal);

                    Promise.resolve().then(() => {
                        setScenarioName('');
                        setSelectedDeal(undefined);
                        queryClient.invalidateQueries({ queryKey: ['deals', selectedOrganizationId!] });
                        onCancel();
                    });
                }, 0);
            } else {
                onConfirm(createNewDeal);
            }
        } catch (error) {
            console.error('Error during save:', error);
        }
    };

    return (
        <NSModal
            {...props}
            modalHeaderText="Save scenario"
            modalHeaderClassName="bg-primary text-white"
            modalFooterButtonText="Save to deal"
            modalFooterCancelButtonText="Cancel"
            modalFooterButtonColor="primary"
            modalFooterButtonClassName="heap-deal-back-of-envelope-save-scenario"
            modalFooterCancelButtonFunction={handleCancel}
            modalFooterButtonFunction={handleSave}
            modalFooterIsButtonFunctionDisabled={!scenarioName || !scenarioName.trim() || !!validationError}
            customFooter={
                selectedDeal ? undefined : (
                    <div>
                        <NSButton text="Cancel" callback={handleCancel} outline />
                        <NSButton
                            className="mx-2 heap-deal-back-of-envelope-save-scenario"
                            color="secondary"
                            text="Save"
                            callback={handleSave}
                            disabled={!scenarioName || !scenarioName.trim() || !!validationError}
                        />
                        <NSButton
                            className="heap-deal-back-of-envelope-create-deal-from-scenario"
                            color="primary"
                            text="Create new deal from scenario"
                            callback={() => handleSave(true)}
                            disabled={!scenarioName || !scenarioName.trim() || !!validationError}
                        />
                    </div>
                )
            }
        >
            <NSInput
                id="scenarioName"
                type="text"
                label="Scenario name"
                name="scenarioName"
                placeholder="Enter name"
                value={scenarioName}
                dataTestId="scenarioName"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScenarioName(e.target.value)}
                inputClassName="mb-2"
                required
                showErrorMsg={!!validationError}
                errorMsg={validationError}
                invalid={!!validationError}
            />
            <NSLabel>Save to deal</NSLabel>
            <NSSelect
                name="deals"
                options={dealOptions || []}
                value={selectedDeal}
                onChange={option => handleDealSelection(option?.value || null)}
                placeholder="Select deal"
            />
            <RenderIf isTrue={!selectedDeal}>
                <small>If no deal is selected, this scenario will be saved as Uncategorized.</small>
            </RenderIf>
        </NSModal>
    );
};

export default SaveModal;
